<template>
  <hierarchical-item-selector
    recursive
    v-model="value"
    v-bind:options="options"
    v-bind:disabled="disabled"
    v-bind:id-prefix="modifiedIdPrefix"
    v-bind:selected-all-label="selectedAllLabel"
    v-bind:get-selected-text="getSelectedText"
    v-bind:caption="
      $t(
        'appIndoorSiteAndBuildingSelector.message.selectIndividuallyExceptForAll'
      )
    "
    v-bind:size="size"
    v-on:change="$_onChange"
    v-on:selected="$_onSelected"
  >
    <template v-slot:item-label="{ item }">
      <span class="wrapper">
        <icon-site v-if="item.type === VALUE_TYPE.INDOOR_SITE" class="icon" />
        <icon-building v-else class="icon" />
        <span class="text">
          {{ item.label }}
        </span>
      </span>
    </template>
  </hierarchical-item-selector>
</template>

<script>
import { HierarchicalItemSelector } from 'ifield-core-components'
import IconSite from '@/assets/ifield/ic_site.svg?component'
import IconBuilding from '@/assets/ifield/ic_building.svg?component'
import i18n from '@/i18n.js'
import { useHandleApiError } from '@/composables/UseHandleApiError'
import { mapGetters } from 'vuex'

// 値種別コード
const VALUE_TYPE = {
  INDOOR_SITE: 'indoorSiteId',
  INDOOR_BUILDING: 'indoorBuildingId',
}

export default {
  name: 'AppIndoorSiteAndBuildingSelector',
  components: {
    HierarchicalItemSelector,
    IconSite,
    IconBuilding,
  },
  props: {
    /**
     * 「すべてのサイト・ビル」を選択している時に表示されるラベル
     */
    selectedAllLabel: {
      type: String,
      default: i18n.t(
        'appIndoorSiteAndBuildingSelector.allIndoorSitesAndBuildings'
      ),
    },
    /**
     * サイズ
     * @values sm, md, lg
     */
    size: {
      type: String,
      default: 'sm',
    },
    /**
     * エレメントIDのprefix
     */
    idPrefix: {
      type: String,
      default: '',
    },
    /**
     * サイト選択値
     */
    indoorSiteIds: {
      type: Array,
      default: null,
    },
    /**
     * ビル選択値
     */
    indoorBuildingIds: {
      type: Array,
      default: null,
    },
    /**
     * 担当ユーザーID
     */
    userIds: {
      type: Array,
      default: null,
    },
  },
  emits: [
    /**
     * 変更イベント
     * @param {Object} value 値
     * @param {string} text 表示テキスト
     */
    'change',
    /**
     * 選択イベント
     * @param {Object} value 値
     * @param {string} text 表示テキスト
     */
    'selected',
  ],
  setup() {
    const { handleApiError } = useHandleApiError()
    return { VALUE_TYPE, handleApiError }
  },
  data: function () {
    return {
      /**
       * 値(ローカル保持用)
       * @type {Object}
       */
      value: null,
    }
  },
  computed: {
    ...mapGetters('indoorSite', ['indoorSiteMap']),
    ...mapGetters('indoorBuilding', ['indoorBuildingMap']),
    /**
     * エレメントIDのprefix
     * @type {string}
     */
    modifiedIdPrefix: function () {
      const base = 'site-building'
      return this.idPrefix ? `${this.idPrefix}-${base}` : base
    },
    /**
     * 選択肢になるビルデータ
     * @type {Array.<Object>}
     */
    indoorSites: function () {
      if (!this.userIds) return this.$store.getters['indoorSite/indoorSites']
      return this.$store.getters['indoorSite/indoorSites'].filter((site) =>
        site.indoor_site_user_relations.some((user) =>
          this.userIds.includes(user.user_id)
        )
      )
    },
    /**
     * サイトに紐付いていないビル
     * @type {Array.<Object>}
     */
    indoorBuildingsNotInSite: function () {
      return this.$store.getters['indoorBuilding/indoorBuildings'].filter(
        (building) => !building.indoor_sites?.length
      )
    },
    /**
     * サイトとビルの関連({ indoorSiteId, indoorBuildingIds })
     * @type {Array.<Object>}
     */
    indoorSiteIndoorBuildingRelation: function () {
      const result = this.indoorSites.map((site) => {
        const indoorBuildingIds =
          site.indoor_site_indoor_building_relations.map(
            (building) => building.id
          )
        return { indoorSiteId: site.id, indoorBuildingIds }
      })

      result.push({
        indoorSiteId: null,
        indoorBuildingIds: this.indoorBuildingsNotInSite.map(
          (building) => building.id
        ),
      })

      return result
    },
    /**
     * 選択肢
     * @type {Array.<Object>}
     */
    options: function () {
      return this.indoorSiteIndoorBuildingRelation.flatMap(
        ({ indoorSiteId, indoorBuildingIds }) => {
          if (indoorSiteId !== null) {
            return this.$_makeIndoorSiteOption(indoorSiteId, indoorBuildingIds)
          } else {
            return indoorBuildingIds.map(this.$_makeIndoorBuildingOption)
          }
        }
      )
    },
    /**
     * 非活性フラグ
     * @type {Object}
     */
    disabled: function () {
      let selectedChildBuildings
      if (this.value) {
        selectedChildBuildings = (
          this.value[VALUE_TYPE.INDOOR_SITE] || []
        ).flatMap((id) => {
          const { indoorBuildingIds } =
            this.indoorSiteIndoorBuildingRelation.find(
              (rel) => rel.indoorSiteId === id
            ) || {}
          return indoorBuildingIds || []
        })
      } else {
        selectedChildBuildings = this.indoorSiteIndoorBuildingRelation
          .filter(({ indoorSiteId }) => indoorSiteId)
          .flatMap(({ indoorBuildingIds }) => indoorBuildingIds)
      }
      return {
        [VALUE_TYPE.INDOOR_SITE]: [],
        [VALUE_TYPE.INDOOR_BUILDING]: [...new Set(selectedChildBuildings)],
      }
    },
  },
  watch: {
    indoorSiteIds: {
      handler: function () {
        this.$_applyToValue()
      },
      immediate: true,
    },
    indoorBuildingIds: {
      handler: function () {
        this.$_applyToValue()
      },
      immediate: true,
    },
  },
  created: function () {
    this.$_updateIndoorBuildings()
    this.$_updateIndoorSites()
  },
  methods: {
    /**
     * 選択値の表示テキスト生成(AppHierarchicalItemSelector への biniging 用)
     * @param {Objcet[]} options 選択肢データ
     * @param {Object} value 選択値
     * @returns {string} 選択値の表示テキスト
     */
    getSelectedText: function (options, value) {
      if (!value) return ''

      const { indoorSiteId, indoorBuildingId } = value
      const results = []

      // サイト選択分
      const collectedBuildingIdSet = new Set()
      for (const sid of indoorSiteId || []) {
        const site = options.find(
          (o) => o.type === VALUE_TYPE.INDOOR_SITE && o.id === sid
        )
        if (!site) continue
        if (site.children.length) {
          results.push(
            `${site.label}(${site.children
              .map((b) => b.shortLabel || b.label)
              .join(',')})`
          )
          for (const { id } of site.children) {
            collectedBuildingIdSet.add(id)
          }
        } else {
          results.push(site.label)
        }
      }

      // ビル選択分
      // サイト選択で表示された分は除く
      const buildingIds = (indoorBuildingId || []).filter(
        (id) => !collectedBuildingIdSet.has(id)
      )
      for (const bid of buildingIds) {
        const building = this.indoorBuildingMap.get(bid)
        if (building) {
          results.push(building.short_name || building.name)
        }
      }

      return results.join(', ')
    },

    /**
     * ビルデータ更新
     */
    $_updateIndoorBuildings: async function () {
      try {
        await this.$store.dispatch(
          'indoorBuilding/updateIndoorBuildingsWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info(
            'failed to fetch indoor buildings. use fetched before.'
          )
        })
      }
    },

    /**
     * サイトデータ更新
     */
    $_updateIndoorSites: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.info('failed to fetch indoor sites. use fetched before.')
        })
      }
    },

    /**
     * サイトの選択肢データ作成
     * @param {number} indoorSiteId サイトID
     * @param {Array.<number>} indoorBuildingIds サイトに紐づくビルID配列
     * @returns {Object} サイトの選択肢データ
     */
    $_makeIndoorSiteOption: function (indoorSiteId, indoorBuildingIds) {
      const name =
        this.indoorSiteMap.get(indoorSiteId)?.site_name || `${indoorSiteId}`
      const childIndoorBuildings = indoorBuildingIds?.map(
        this.$_makeIndoorBuildingOption || []
      )
      return {
        type: VALUE_TYPE.INDOOR_SITE,
        id: indoorSiteId,
        label: name,
        children: childIndoorBuildings,
      }
    },

    /**
     * ビルの選択肢データ作成
     * @param {number} indoorBuildingId ビルID
     * @returns {Object} ビルの選択肢データ
     */
    $_makeIndoorBuildingOption: function (indoorBuildingId) {
      const name =
        this.indoorBuildingMap.get(indoorBuildingId)?.name ||
        `${indoorBuildingId}`
      const shortName =
        this.indoorBuildingMap.get(indoorBuildingId)?.short_name || ''
      return {
        type: VALUE_TYPE.INDOOR_BUILDING,
        id: indoorBuildingId,
        label: name,
        shortLabel: shortName,
      }
    },

    /**
     * propsの選択値をvalueに反映
     */
    $_applyToValue: function () {
      if (this.indoorBuildingIds == null && this.indoorSiteIds == null) {
        this.value = null
      } else {
        // 片方だけ null という選択はできないので null は空配列に置き換える
        this.value = {
          [VALUE_TYPE.INDOOR_BUILDING]:
            this.indoorBuildingIds == null ? [] : [...this.indoorBuildingIds],
          [VALUE_TYPE.INDOOR_SITE]:
            this.indoorSiteIds == null ? [] : [...this.indoorSiteIds],
        }
      }
    },

    /**
     * 変更時処理
     * @param {Object} value 変更値
     * @param {string} text 表示テキスト
     */
    $_onChange: function (value, text) {
      this.$emit('change', value, text)
    },

    /**
     * 選択時処理
     * @param {Object} value 選択値
     * @param {string} text 選択値テキスト
     */
    $_onSelected: function (value, text) {
      this.$emit('selected', value, text)
    },
  },
}
</script>

<style lang="scss" scoped>
.wrapper {
  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 4px;

  .icon {
    flex-shrink: 0;
    fill: $text-color;
  }

  .text {
    color: $text-color;
    font-size: $font-size-middle;
  }
}
</style>
